import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtEUR } from "@/lib/api";
import { shortConcept } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCheck, Play, Link2, Percent, Clock, Tag } from "lucide-react";
import { TxNotesDialog } from "@/components/TxNotesDialog";
import { toast } from "sonner";
import { KIND_LABELS } from "@/components/conciliacion/shared";
import { ManualDialog } from "@/components/conciliacion/ManualDialog";
import { CategorizeDialog } from "@/components/conciliacion/CategorizeDialog";
import { SuggestionsDialog } from "@/components/conciliacion/SuggestionsDialog";
import { PendingTable } from "@/components/conciliacion/PendingTable";
import { ReconciledTable } from "@/components/conciliacion/ReconciledTable";
import { IgnoredTable } from "@/components/conciliacion/IgnoredTable";
import { CategorizedTable } from "@/components/conciliacion/CategorizedTable";

export default function Conciliacion() {
  const { user, org } = useAuth();
  const canAct = user?.role === "ADMIN" || user?.role === "FINANCE";
  const [data, setData] = useState({ transactions: [], kpis: {} });
  const [reconciled, setReconciled] = useState([]);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [manualTx, setManualTx] = useState(null);
  const [notesTx, setNotesTx] = useState(null);
  const [ignored, setIgnored] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [runAccount, setRunAccount] = useState("all");
  const [categorized, setCategorized] = useState([]);
  const [categories, setCategories] = useState([]);
  const [typeFilter, setTypeFilter] = useState("todos");
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [monthFilter, setMonthFilter] = useState("todos");
  const [searchTx, setSearchTx] = useState("");
  const [selected, setSelected] = useState({});
  const [catTxs, setCatTxs] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [searchParams] = useSearchParams();
  const focusTx = searchParams.get("tx");
  const [tab, setTab] = useState(focusTx ? "done" : "pending");

  const load = useCallback(() => {
    api.get("/reconciliation/pending").then((r) => setData(r.data)).catch(() => {});
    api.get("/reconciliation/pending?status=ignorado").then((r) => setIgnored(r.data.transactions)).catch(() => {});
    api.get("/reconciliation/pending?status=categorizado").then((r) => setCategorized(r.data.transactions)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/reconciliation/suggestions").then((r) => setSuggestions(r.data)).catch(() => {});
    api.get("/reconciliation/list").then((r) => setReconciled(r.data)).catch(() => {});
    api.get("/banks/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep link: /conciliacion?tx=<bank_transaction_id> → pestaña correspondiente + foco
  useEffect(() => {
    if (!focusTx) return;
    if (!reconciled.length && !categorized.length && !ignored.length && !(data?.transactions || []).length) return;
    setSearchTx("");
    let targetTab = "pending";
    let testid = null;
    const rec = reconciled.find((r) => r.bank_transaction_id === focusTx);
    if (rec) { targetTab = "done"; testid = `recon-done-${rec.id}`; }
    else if (categorized.some((t) => t.id === focusTx)) { targetTab = "categorized"; testid = `recon-categorized-${focusTx}`; }
    else if (ignored.some((t) => t.id === focusTx)) { targetTab = "ignored"; testid = `recon-ignored-${focusTx}`; }
    else if ((data?.transactions || []).some((t) => t.id === focusTx)) { testid = `recon-row-${focusTx}`; }
    setTab(targetTab);
    if (testid) {
      setTimeout(() => {
        document.querySelector(`[data-testid="${testid}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    }
  }, [focusTx, reconciled, categorized, ignored, data]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/reconciliation/run${runAccount !== "all" ? `?account_id=${runAccount}` : ""}`);
      toast.success(`Matching: ${r.data.auto} automáticas · ${r.data.review} a revisión · ${r.data.no_match} sin match`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en el matching");
    } finally { setRunning(false); }
  };

  const acceptCandidate = async (tx, cand) => {
    try {
      // Sin importes: el backend asigna el pendiente completo de cada factura del candidato
      await api.post("/reconciliation/manual", {
        transaction_id: tx.id,
        allocations: cand.invoice_ids.map((id) => ({ invoice_id: id })),
      });
      toast.success(cand.type === "grouped" ? "Pago agrupado conciliado" : "Conciliado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al conciliar");
    }
  };

  const unreconcile = async (txId) => {
    if (!window.confirm("¿Desconciliar? Los pagos asociados se reversarán (el movimiento bancario no se borra).")) return;
    try {
      await api.post(`/reconciliation/${txId}/unreconcile`);
      toast.success("Desconciliado — pagos reversados");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al desconciliar");
    }
  };

  const ignore = async (txId) => {
    try {
      await api.post(`/reconciliation/${txId}/ignore`, {});
      toast.success("Movimiento actualizado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar el movimiento");
    }
  };

  const uncategorize = async (txId) => {
    try {
      await api.post(`/reconciliation/${txId}/uncategorize`);
      toast.success("Categorización revertida");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al revertir");
    }
  };

  const visibleTxs = data.transactions
    .filter((tx) => typeFilter === "todos" || (typeFilter === "gastos" ? tx.amount < 0 : tx.amount > 0))
    .filter((tx) => monthFilter === "todos" || (tx.date || "").slice(0, 7) === monthFilter)
    .filter((tx) => {
      const q = searchTx.trim().toLowerCase();
      return !q || (tx.concept || "").toLowerCase().includes(q) || (tx.reference || "").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sort.key === "concept") cmp = shortConcept(a.concept || "").localeCompare(shortConcept(b.concept || ""), "es", { sensitivity: "base" });
      else if (sort.key === "amount") cmp = Math.abs(a.amount) - Math.abs(b.amount);
      else if (sort.key === "account") cmp = (a.account_name || "").localeCompare(b.account_name || "", "es", { sensitivity: "base" });
      else if (sort.key === "candidates") cmp = (a.match_candidates || []).length - (b.match_candidates || []).length;
      else cmp = (a.date || "").localeCompare(b.date || "");
      return sort.dir === "asc" ? cmp : -cmp;
    });
  const months = [...new Set(data.transactions.map((t) => (t.date || "").slice(0, 7)).filter(Boolean))].sort().reverse();
  const selectedTxs = visibleTxs.filter((tx) => selected[tx.id]);
  const sugMap = Object.fromEntries(suggestions.map((s) => [s.tx_id, s]));
  const clickSort = (key, defDir) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: defDir }));
  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleSelectAll = () => {
    const all = visibleTxs.length > 0 && visibleTxs.every((tx) => selected[tx.id]);
    const next = { ...selected };
    visibleTxs.forEach((tx) => { next[tx.id] = !all; });
    setSelected(next);
  };
  const catName = (id) => categories.find((c) => c.id === id)?.name || "";
  const branchName = (id) => (org?.branches || []).find((b) => b.id === id)?.name || "";

  // Búsqueda y ordenación compartidas por todas las pestañas
  const txSearch = (concept, extra = "") => {
    const q = searchTx.trim().toLowerCase();
    return !q || (concept || "").toLowerCase().includes(q) || (extra || "").toLowerCase().includes(q);
  };
  const applySort = (list, accessors) => [...list].sort((a, b) => {
    const get = accessors[sort.key] || accessors.date;
    const va = get(a); const vb = get(b);
    const cmp = typeof va === "number" ? va - vb
      : String(va ?? "").localeCompare(String(vb ?? ""), "es", { sensitivity: "base" });
    return sort.dir === "asc" ? cmp : -cmp;
  });
  const visibleReconciled = applySort(
    reconciled.filter((r) => txSearch(r.tx_concept, (r.invoices_info || []).map((i) => i.supplier_name).join(" "))),
    { date: (r) => r.tx_date, concept: (r) => shortConcept(r.tx_concept),
      invoices: (r) => (r.invoices_info || []).map((i) => `${i.supplier_name} ${i.invoice_number || ""}`).join(" + "),
      amount: (r) => Math.abs(r.amount), type: (r) => (r.type === "auto" ? `auto ${r.score}` : "manual") });
  const visibleIgnored = applySort(
    ignored.filter((r) => txSearch(r.concept, r.account_name)),
    { date: (r) => r.date, concept: (r) => shortConcept(r.concept), account: (r) => r.account_name || "",
      amount: (r) => Math.abs(r.amount) });
  const visibleCategorized = applySort(
    categorized.filter((r) => txSearch(r.concept, `${catName(r.pnl_category_id)} ${branchName(r.branch_id)}`)),
    { date: (r) => r.date, concept: (r) => shortConcept(r.concept),
      kind: (r) => KIND_LABELS[r.pnl_kind] || r.pnl_kind,
      catbranch: (r) => `${catName(r.pnl_category_id)} ${branchName(r.branch_id)}`,
      amount: (r) => Math.abs(r.amount) });

  const k = data.kpis || {};

  return (
    <div className="space-y-6" data-testid="conciliacion-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Conciliación bancaria</h1>
          <p className="text-sm text-slate-500 mt-1">Match ≥95 automático · 80-94 a revisión · nunca se borra el movimiento original</p>
        </div>
        {canAct && (
          <div className="flex gap-2 flex-wrap">
            <Select value={runAccount} onValueChange={setRunAccount}>
              <SelectTrigger data-testid="run-account-select" className="w-full sm:w-56 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las cuentas</SelectItem>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="recon-suggestions-button" variant="outline" onClick={() => setShowSug(true)}
              disabled={suggestions.length === 0} className="gap-2">
              <Tag className="w-4 h-4" /> Sugerencias ({suggestions.length})
            </Button>
            <Button data-testid="run-matching-button" onClick={run} disabled={running}
              className="bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-2">
              <Play className={`w-4 h-4 ${running ? "animate-pulse" : ""}`} /> {running ? "Buscando matches..." : "Ejecutar matching"}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Pendientes", value: k.pendientes ?? 0, icon: Clock, testid: "recon-pending" },
          { label: "Importe pendiente", value: fmtEUR(k.importe_pendiente ?? 0), icon: Link2, testid: "recon-pending-amount" },
          { label: "Conciliados", value: k.conciliados ?? 0, icon: CheckCheck, testid: "recon-done" },
          { label: "% conciliación", value: `${k.pct ?? 0}%`, icon: Percent, testid: "recon-pct" },
        ].map((c) => (
          <Card key={c.testid} data-testid={c.testid}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">{c.label}</CardTitle>
              <c.icon className="w-4 h-4 text-slate-400" />
            </CardHeader>
            <CardContent><div className="text-2xl font-extrabold font-num tracking-tight">{c.value}</div></CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="recon-tabs" className="flex-wrap h-auto justify-start">
          <TabsTrigger value="pending" data-testid="tab-recon-pending">Pendientes</TabsTrigger>
          <TabsTrigger value="done" data-testid="tab-recon-done">Conciliados</TabsTrigger>
          <TabsTrigger value="ignored" data-testid="tab-recon-ignored">Ignorados{ignored.length > 0 ? ` (${ignored.length})` : ""}</TabsTrigger>
          <TabsTrigger value="categorized" data-testid="tab-recon-categorized">Categorizados{categorized.length > 0 ? ` (${categorized.length})` : ""}</TabsTrigger>
        </TabsList>

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Input data-testid="recon-search" placeholder="Buscar concepto o referencia..." value={searchTx}
            onChange={(e) => setSearchTx(e.target.value)} className="h-9 w-64" />
          <span className="text-xs text-slate-400">La búsqueda se aplica a todas las pestañas</span>
        </div>

        <TabsContent value="pending" className="mt-4">
          <PendingTable visibleTxs={visibleTxs} months={months} typeFilter={typeFilter} setTypeFilter={setTypeFilter}
            monthFilter={monthFilter} setMonthFilter={setMonthFilter} selectedTxs={selectedTxs} canAct={canAct}
            selected={selected} toggleSelect={toggleSelect} toggleSelectAll={toggleSelectAll}
            expanded={expanded} setExpanded={setExpanded} focusTx={focusTx} sugMap={sugMap}
            sort={sort} clickSort={clickSort}
            onManual={setManualTx} onCategorize={(tx) => setCatTxs([tx])} onNotes={setNotesTx}
            onIgnore={ignore} onAccept={acceptCandidate}
            onBulkCategorize={() => setCatTxs(selectedTxs)} onBulkClear={() => setSelected({})} />
        </TabsContent>

        <TabsContent value="done" className="mt-4">
          <ReconciledTable rows={visibleReconciled} totalCount={reconciled.length} canAct={canAct}
            focusTx={focusTx} sort={sort} clickSort={clickSort} onUnreconcile={unreconcile} />
        </TabsContent>

        <TabsContent value="ignored" className="mt-4">
          <IgnoredTable rows={visibleIgnored} totalCount={ignored.length} canAct={canAct}
            focusTx={focusTx} sort={sort} clickSort={clickSort} onRestore={ignore} />
        </TabsContent>

        <TabsContent value="categorized" className="mt-4">
          <CategorizedTable rows={visibleCategorized} totalCount={categorized.length} canAct={canAct}
            focusTx={focusTx} sort={sort} clickSort={clickSort} onUncategorize={uncategorize}
            catName={catName} branchName={branchName} />
        </TabsContent>
      </Tabs>

      <ManualDialog tx={manualTx} open={!!manualTx} onClose={() => setManualTx(null)} onDone={load} />
      <TxNotesDialog txId={notesTx?.id} txLabel={notesTx?.concept} open={!!notesTx}
        onClose={() => setNotesTx(null)} onChanged={load} />
      <CategorizeDialog txs={catTxs} open={catTxs.length > 0} onClose={() => { setCatTxs([]); setSelected({}); }} onDone={load}
        categories={categories} branches={org?.branches || []} sugMap={sugMap} />
      <SuggestionsDialog suggestions={suggestions} open={showSug} onClose={() => setShowSug(false)} onDone={load} />
    </div>
  );
}
